import * as React from "react";
import {cx} from "emotion";

import './image-effects-mode.scss';

import {cs}                           from "@Common/react/chain-services";
import {consumeContext}               from "@Common/react/context";
import {fragments}                    from "@Common/react/fragments";
import {Invoke}                       from "@Common/react/invoke";
import {UseState}                     from "@Common/react/use-state";
import {ConfirmDialog}                from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {ImageEditor}                  from "@Common/ui-components/image-editor/image-editor";
import {Responsive2}                  from "@Common/ui-components/responsive-2";
import {addURLParameterToWindow}      from "@Common/utils/add-url-parameter";
import {chain}                        from "@Common/utils/fs";
import {removeURLParameterFromWindow} from "@Common/utils/remove-url-parameter";

import {Controls}      from "./controls/controls";
import {FileNameInput} from "./file-name-input/file-name-input";
import {Zoom}          from "./zoom/zoom";

export const ImageEffectsMode = ({modal, data, typeLabel, onImageEdit, next}) => cs(
    consumeContext("apis"),
    (_, next) => fragments(
        Invoke({action: () => addURLParameterToWindow({parameter: "action", value: "image-effects"})}),
        next()
    ),
    ["updating", (_, next) => UseState({next, initValue: false})],
    ["nameState", (_, next) => UseState({
        getInitValue: () => data.name,
        next,
    })],
    ["imageEditor", (_, next) => ImageEditor({
        url: chain(
            data.printUrl || data.imageUrl,
            (url) => data.fileType === "pdf" ? mapUrlAsImgProxy(url) : url
        ),
        next,
    })],
    ["confirmCancel", (_, next) => ConfirmCancel({next})],
    ["responsive", (_, next) => Responsive2({
        translate: (w) => w < 1024 ? "mobile" : "desktop",
        next,
    })],
    ({apis, imageEditor, updating, nameState, confirmCancel, responsive}) => next({
        width: 913,
        onPassiveClose: async () => {
            if (await confirmCancel()) {
                removeURLParameterFromWindow({parameter: 'action'});
                return true;
            }
            return false;
        },

        content: (
            <div className={cx("image-effects-mode-d8u", responsive)}>
                <div className="header">
                    <div className="left">
                        <div {...{
                            className: "title",
                            "xd-qa-type": "label",
                            "xd-qa-name": `Edit ${typeLabel}`,
                        }}>
                            {`Edit ${typeLabel}`}
                        </div>

                        {FileNameInput({
                            state: nameState,
                            fileType: data.fileType,
                            className: "file-name-input",
                        })}
                    </div>

                    {HeaderControls({
                        undo: imageEditor.controls.undo,
                        redo: imageEditor.controls.redo,
                    })}
                </div>
                <div className="content">

                    <div className="main">
                        {imageEditor.renderMain({
                            mobile: responsive === "mobile",
                        })}
                    </div>

                    {!imageEditor.controls.mode.current && imageEditor.controls.reset.canDo && (
                        <button {...{
                            className: "restore",
                            onClick: imageEditor.controls.reset.invoke,
                            "xd-qa-name":"restore",
                            "xd-qa-type":"button"
                        }}>
                            <i className="fa fa-eraser"/>
                            Restore
                        </button>
                    )}

                    {Zoom({
                        control: imageEditor.controls.zoom,
                        useSlider: responsive === "mobile",
                    })}

                    <div className="mode-panel">
                        {imageEditor.modePanel?.({
                            mobile: responsive === "mobile",
                        })}
                    </div>

                </div>

                <div className="footer">

                    {Controls({
                        className: "controls",
                        controls: imageEditor.controls,
                        spread: responsive === "mobile",
                    })}

                    <div className="buttons">

                        <button {...{
                            disabled: !(imageEditor.changed || nameState.value !== data.name) || updating.value,
                            className: "save",
                            "xd-qa-type": "button",
                            "xd-qa-name": "Save",
                            onClick: async () => {
                                updating.onChange(true);
                                const mimeString = "image/png";
                                const content = await imageEditor.toBlob({mimeString});
                                await onImageEdit({mimeString, name: nameState.value, content, printUrl: data.printUrl, defaultSlot: data.defaultSlot});
                                updating.onChange(false);
                                modal.close();
                            },
                        }}>
                            Save
                        </button>
                    </div>
                </div>
            </div>
        ),
    }),
);

const HeaderControls = ({undo, redo}) => {
    const Button = ({direction, control}) => (
        <i {...{
            className: cx("fa", "fa-rotate-" + direction, {disabled: !control.canDo}),
            onClick: control.invoke,
            "xd-qa-type":"icon",
            "xd-qa-name":direction + " control"

        }}/>
    );
    return (
        <div className="controls">
            {Button({
                direction: "left",
                control: undo,
            })}
            {Button({
                direction: "right",
                control: redo,
            })}
        </div>
    );
};

const ConfirmCancel = ({next}) => cs(
    ["confirmDialog", ({}, next) => ConfirmDialog({next})],
    ({confirmDialog}) => next(async () => {
        return await confirmDialog.show({
            actionName: "close",
            message: "Your change has not been saved",
            // buttons: {
            //     confirmLabel: "Close",
            // },
        });
    }),
);

const mapUrlAsImgProxy = (url:string): string => (
    `https://imgproxy.xpressdocs.com/image/png/2000x2000/${encodeURIComponent(url)}`
);

